import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Component,
  ContainerItem,
  isContainerItem,
  Page,
} from '@bloomreach/spa-sdk';
import { CaveatDataService } from '@products/caveats/services/caveat-data.service';
import { DebugService } from '@services/debug.service';
import { CaveatCollection } from '@types';
import { Logger } from '@utils/logger';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { debounceTime, delay, map, shareReplay, tap } from 'rxjs/operators';
import { FootnoteService } from 'src/app/types/footnote-service.interface';
import { ProximalService } from 'src/app/types/proximal-service.interface';

const logger = Logger.getLogger('CaveatService');

/**
 * Caveats Service to provide proximal, footnote and legal caveats to all pages.
 *
 * Relies on 3 sources of data:
 *  1. caveat feed for the taxonomy country/language.
 *  2. fund data (if provided) to filter on specific fund details.
 *
 * The caveat feed loads ALL the caveats for the site.
 * This is processed to make it easier to filter the caveats for a specific page by type.
 * Individual caveats then subscribe and receive the data they need based on placement parameter.
 *
 * Footnote Order
 * ==============
 * Cant work out the footnote order from order of registration as components load asynchronously.
 * Cant work out the footnote order from a query selector as components in unfocussed tabs will not be rendered.
 * So we assume registration order within a component is in page order.
 * And we encode the Component name within the placement.
 * And we sort based on the component order from the resourceapi response (which works as long as the page layouts maintain that order).
 */
@Injectable({
  providedIn: 'root',
})
export class CaveatService implements FootnoteService, ProximalService {
  filteredCaveats$: Observable<CaveatCollection>;
  filteredFootnotes$: Observable<any>;
  fundData: any = {};
  fundData$ = new BehaviorSubject(this.fundData);
  registeredFootnotesPlacements: string[] = [];
  registeredFootnotesPlacements$: BehaviorSubject<
    string[]
  > = new BehaviorSubject(this.registeredFootnotesPlacements);
  currentFundId: string;
  showDummyCaveats = true;
  footnotesByComponent: { [comp: string]: string[] } = {};
  componentOrder: string[] = [];
  footnoteOrder: string[] = [];
  isSiteIntl = false;

  constructor(
    caveatDataService: CaveatDataService,
    private debugService: DebugService,
    private router: Router
  ) {
    this.debugService
      .isShowDummyCaveats$()
      .subscribe((flag) => (this.showDummyCaveats = flag));

    this.filteredCaveats$ = combineLatest([
      caveatDataService.getData$(),
      this.fundData$,
    ]).pipe(
      tap((vals) => logger.debug('sources loaded', vals)),
      map((vals) => {
        return this.filterCaveats(vals);
      }),
      tap((caveats) => logger.debug('caveats filtered', caveats)),
      shareReplay(1)
    );

    this.filteredFootnotes$ = combineLatest([
      this.registeredFootnotesPlacements$.pipe(debounceTime(200)),
      this.filteredCaveats$.pipe(
        map((caveats) => {
          return caveats.footnotes.reduce((acc, footnote) => {
            // aggregate the footnotes by placement.
            footnote.placements.forEach((placement) => {
              if (acc[placement] === undefined) {
                acc[placement] = [footnote];
              } else {
                acc[placement].push(footnote);
              }
            });
            return acc;
          }, {});
        })
      ),
    ]).pipe(
      map(([registeredPlacements, footnotesByPlacement]) => {
        const populatedPlacements = registeredPlacements.filter(
          (registeredPlacement) =>
            footnotesByPlacement[registeredPlacement] !== undefined
        );

        if (populatedPlacements) {
          // need to reindex
          populatedPlacements.forEach((registeredPlacement) =>
            footnotesByPlacement[registeredPlacement].forEach(
              (footnote) => delete footnote.index
            )
          );
          const orderedPopulatedPlacements = this.sortPlacementsByPageOrder(
            populatedPlacements,
            this.footnoteOrder
          );
          let i = 1;
          // work out indexes based on order of placements and number of footnotes for each placement
          const ordered = orderedPopulatedPlacements.map(
            (registeredPlacement) => ({
              placement: registeredPlacement,
              content: footnotesByPlacement[registeredPlacement].filter(
                (footnote) => footnote.index === undefined
              ), // only want content for new footnotes
              indexes: footnotesByPlacement[registeredPlacement]
                .map((footnote) => {
                  // if the footnote already has an index (because it is in another placement) then use that.
                  if (footnote.index === undefined) {
                    footnote.index = String(i++);
                  }
                  return footnote.index;
                })
                .sort((a, b) => {
                  return +a - +b;
                }),
            })
          );
          const mapped = ordered.reduce((acc, placement) => {
            // convert to object with placement as key
            acc[placement.placement] = placement;
            return acc;
          }, {});
          return {
            ordered,
            mapped,
          };
        }
        return {
          ordered: [],
          mapped: {},
        };
      }),
      tap((footnotes) => logger.debug('filteredFootnotes', footnotes)),
      shareReplay(1)
    );
  }

  private sortPlacementsByPageOrder(
    populatedPlacements: string[],
    placementsOrder: string[]
  ): string[] {
    return populatedPlacements.sort((a, b) => {
      let ai = placementsOrder.indexOf(a);
      let bi = placementsOrder.indexOf(b);
      if (ai === -1) {
        ai = 99999;
      }
      if (bi === -1) {
        bi = 99999;
      }
      if (ai < bi) {
        return -1;
      }
      if (ai > bi) {
        return 1;
      }
      return 0;
    });
  }

  /**
   * Called by the legal caveats component
   * @param page - the current page
   * @param fundId - set by the route or component
   */
  setCaveatFilter(fundId?: string): void {
    if (!fundId) {
      logger.debug('not a fund specific page');
      // not a fund specific page
      this.fundData = {};
      this.fundData$.next(this.fundData); // should we remove caveats with a fund configured?
      return;
    }
    if (fundId !== this.currentFundId) {
      logger.debug('fund specific page', fundId);
      this.currentFundId = fundId;
      this.fundData$.next({
        fundId,
      });
    }
  }

  /**
   * filter the set of all caveats so only those applicable to this page are available
   */
  private filterCaveats(vals: any[]): CaveatCollection {
    const [caveats, fundData] = vals;
    if (fundData.fundId === undefined) {
      return {
        legals: caveats.legals.filter((legal) =>
          this.isCaveatApplicable(legal)
        ),
        proximals: caveats.proximals.filter((proximal) =>
          this.isCaveatApplicable(proximal)
        ),
        footnotes: caveats.footnotes.filter((footnote) =>
          this.isCaveatApplicable(footnote)
        ),
      };
    }
    return {
      legals: caveats.legals?.filter((legal) =>
        this.isCaveatApplicable(legal, fundData)
      ),
      proximals: caveats.proximals?.filter((proximal) =>
        this.isCaveatApplicable(proximal, fundData)
      ),
      footnotes: caveats.footnotes?.filter((footnote) =>
        this.isCaveatApplicable(footnote, fundData)
      ),
    };
  }

  /**
   * a caveat is applicable if fund tagging match the current context
   */
  private isCaveatApplicable(caveat, fundData?: any): boolean {
    if (fundData === undefined) {
      return this.empty(caveat.fl);
    }
    return this.emptyOrIncludes(caveat.fl, fundData.fundId);
  }

  private emptyOrIncludes(arrayField: string[], matcher: string): boolean {
    return (
      arrayField === undefined ||
      arrayField.length === 0 ||
      arrayField.includes(matcher)
    );
  }

  private empty(arrayField: string[]): boolean {
    return arrayField === undefined || arrayField.length === 0;
  }

  private parseComponentPlacement(componentPlacement: string): string[] {
    if (componentPlacement.indexOf(':') === -1) {
      logger.warn('unidentified component for placement', componentPlacement);
      return ['unidentified', componentPlacement];
    } else {
      return componentPlacement.split(':');
    }
  }
  /**
   * Get the footnotes refs for a particular placement. This also registers the footnote placement.
   */
  getRefs$(componentPlacement: string): Observable<string[]> {
    const [component, placement] = this.parseComponentPlacement(
      componentPlacement
    );
    this.updateFootnoteOrder(component, placement);

    logger.debug('footnote registered', placement);
    if (this.showDummyCaveats) {
      if (!this.registeredFootnotesPlacements.includes(placement)) {
        this.registeredFootnotesPlacements.push(placement);
      }
      return of([
        String(this.registeredFootnotesPlacements.indexOf(placement) + 1),
      ]);
    }
    // wrap filteredFootnotes$ to ensure we remove from registeredFootnotePlacements on unsubscribe
    return new Observable((subscriber) => {
      const sub = this.filteredFootnotes$
        .pipe(
          map((footnotes) => footnotes.mapped[placement]?.indexes || []),
          tap((indexes) => logger.debug('getFootnoteIndexes', indexes))
        )
        .subscribe(subscriber);
      if (!this.registeredFootnotesPlacements.includes(placement)) {
        this.registeredFootnotesPlacements.push(placement);
        this.registeredFootnotesPlacements$.next(
          this.registeredFootnotesPlacements.slice(0)
        );
      }
      return () => {
        sub.unsubscribe();
        // remove registration for this placement
        const index = this.registeredFootnotesPlacements.indexOf(placement);
        if (index > -1) {
          this.registeredFootnotesPlacements.splice(index, 1);
          this.registeredFootnotesPlacements$.next(
            this.registeredFootnotesPlacements
          );
        }
      };
    });
  }

  /**
   * get a list of footnote paragraphs in correct order to show at bottom of page.
   */
  getFootnoteContents$(): Observable<any> {
    if (this.showDummyCaveats) {
      return of(this.registeredFootnotesPlacements).pipe(delay(3000));
    }
    return this.filteredFootnotes$.pipe(
      tap((footnotes) => logger.debug('footnote content', footnotes)),
      map((footnotes) =>
        footnotes.ordered.reduce((combined: any[], placement: any) => {
          return combined.concat(
            placement.content.reduce(
              (contents: string[], footnote) =>
                contents.concat(footnote.content),
              []
            )
          );
        }, [])
      )
    );
  }

  /**
   * get the proximal caveats for a particular placement
   */
  getProximals$(placement: string): Observable<string[]> {
    if (this.showDummyCaveats) {
      return of([`Dummy proximal content for placement: ${placement}`]);
    }
    return this.filteredCaveats$.pipe(
      map((caveats) => {
        return caveats.proximals
          ?.filter((proximal) => proximal.placements.includes(placement))
          .map((proximal) => {
            return !this.isSiteIntl
              ? `<div class="small">${this.fixLocalLinks(
                  proximal.content
                )}</div>`
              : this.fixLocalLinks(proximal.content);
          });
      })
    );
  }

  /**
   * get the legal caveats for a particular placement
   */
  getLegals$(placement: string, directs: string[] = []): Observable<string[]> {
    if (this.showDummyCaveats) {
      return of([`Dummy legal caveat for placement: ${placement}`]);
    }
    return this.filteredCaveats$.pipe(
      map((caveats) =>
        caveats.legals
          ?.filter(
            (legal) =>
              legal.placements.includes(placement) ||
              directs.includes(legal.uuid)
          )
          .map((legal) => legal.content)
      )
    );
  }

  // TODO refactor to reuse component mapper code
  private getProductsPresent(
    page: Page,
    productComponents: string[]
  ): string[] {
    const componentNames = [];
    const components: Component[] = page.getComponent().getChildren();
    this.getAnyProductComponentPresent(
      components,
      productComponents,
      componentNames
    );
    return componentNames.filter((i) => i !== undefined);
  }

  private getAnyProductComponentPresent(
    components: Component[],
    productComponents: string[],
    componentNames: string[]
  ): void {
    components.forEach((item) => {
      if (
        isContainerItem(item) &&
        productComponents.includes((item as ContainerItem).getLabel())
      ) {
        componentNames.push(
          item.getModels().config?.componentType || item.getType()
        );
      }
      const children = item.getChildren();
      if (children?.length) {
        this.getAnyProductComponentPresent(
          children,
          productComponents,
          componentNames
        );
      }
    });
  }

  initialiseFootnotesByComponent(page: Page, components: string[]) {
    this.footnotesByComponent = { unidentified: [] };
    if (page) {
      this.componentOrder = this.getProductsPresent(page, components);
      for (const comp of this.componentOrder) {
        this.footnotesByComponent[comp] = [];
      }
      this.componentOrder.push('unidentified');
    } else {
      this.componentOrder = ['unidentified'];
    }
  }

  private updateFootnoteOrder(component, placement) {
    if (this.footnotesByComponent[component] === undefined) {
      logger.error(`Unknown component: ${component}`);
      component = 'unidentified';
    }
    this.footnotesByComponent[component].push(placement);
    this.footnoteOrder = this.componentOrder
      .map((comp) => this.footnotesByComponent[comp])
      .flat();
  }

  /**
   *
   * @param content links to, say, #important-legal-info need to have current url path prepended
   */
  private fixLocalLinks(content: string): string {
    return content.replace(
      new RegExp(`href=(.)#`, 'gi'),
      `href=$1${this.router.url}#`
    );
  }
}
